import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
    AlertCircle,
    ArrowRight,
    CalendarDays,
    Check,
    CheckCircle2,
    Loader2,
    MapPin,
    Mic,
    MicOff,
    Package,
    Send,
    Sparkles,
    Utensils,
    Users,
    X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import './AICampaignAssistant.scss';
import {
    extractDateRange,
    getCountryDisplayName,
    getFollowUpItems,
    inferCountryFromText,
    normalizeCountryId,
    normalizeGeneratedData,
} from './AICampaignAssistant.utils';

const stripHtml = (value = '') => value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
const hasBookingLanguage = (value = '') => /\b(?:book|books|booked|booking|bookings|reservation|reservations|reserva|reservas|reservar|appointment|appointments|cita|citas|agendar|agendamento|agendamentos)\b/i.test(String(value || ''));
const compactPromptData = (data = {}) => Object.fromEntries(
    Object.entries(data).filter(([, value]) => {
        if (value === undefined || value === null || value === '') return false;
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'object') return Object.keys(value).length > 0;
        return true;
    }),
);

const hasDeliverableSignal = (text = '') => {
    const normalized = text.toLowerCase();
    return [
        'open deliverables',
        'entregables abiertos',
        'entregaveis abertos',
        'creator decides',
        'creadores deciden',
        'criadores decidem',
        'post',
        'posts',
        'story',
        'stories',
        'reel',
        'reels',
    ].some((term) => normalized.includes(term));
};

const hasOpenDeliverablesSignal = (text = '') => {
    const normalized = text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return /open|abiert|livre|as many as|cuantos quiera|quantos quiser|creator decides|creadores deciden|criadores decidem/.test(normalized);
};

const getDeliverableMentions = (text = '') => {
    const normalized = text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const patterns = [
        { id: '1', pattern: /\b(\d+|one|uno|una|um|uma)\s*(?:x\s*)?(?:post|posts|publicacion|publicaciones|publicacao|publicacoes)\b/g },
        { id: '2', pattern: /\b(\d+|one|uno|una|um|uma)\s*(?:x\s*)?(?:story|stories|historia|historias)\b/g },
        { id: '3', pattern: /\b(\d+|one|uno|una|um|uma)\s*(?:x\s*)?(?:reel|reels)\b/g },
    ];
    const textNumbers = {
        one: 1,
        uno: 1,
        una: 1,
        um: 1,
        uma: 1,
    };

    return patterns.reduce((acc, { id, pattern }) => {
        Array.from(normalized.matchAll(pattern)).forEach((match) => {
            const amount = textNumbers[match[1]] || Number(match[1]);
            if (amount > 0) acc[id] = (acc[id] || 0) + amount;
        });
        return acc;
    }, {});
};

const getDeliverableFormats = (text = '') => {
    const normalized = text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const formats = [];
    if (/post|posts|publicacion|publicaciones|publicacao|publicacoes/.test(normalized)) formats.push('1');
    if (/stor(y|ies)|historia|historias/.test(normalized)) formats.push('2');
    if (/reel|reels/.test(normalized)) formats.push('3');
    return Array.from(new Set(formats));
};

const hasTimeRangeSignal = (text = '') => {
    const normalized = text.toLowerCase();
    return [
        'time range',
        'horario',
        'horário',
        'hora',
        'evening',
        'morning',
        'afternoon',
        'noche',
        'manana',
        'mañana',
        'tarde',
        'noite',
    ].some((term) => normalized.includes(term));
};

const hasFollowerSignal = (text = '') => {
    const normalized = text.toLowerCase();
    return [
        'followers',
        'seguidores',
        '3000',
        '3k',
        '15000',
        '15k',
        'friends of the house',
        'sin requisito',
        'no requirement',
        'sem requisito',
    ].some((term) => normalized.includes(term));
};

const hasSponsorSignal = (text = '') => {
    const normalized = text.toLowerCase();
    return [
        'sponsor',
        'sponsored',
        'sponsorship',
        'patrocinar',
        'patrocinio',
        'patrocínio',
    ].some((term) => normalized.includes(term));
};

const inferFieldsFromConversation = (data = {}, conversationText = '', countriesList = []) => {
    const normalized = conversationText.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const inferred = { ...data };
    const dateRange = extractDateRange(conversationText);

    if (dateRange.from_date && dateRange.to_date) {
        inferred.from_date = dateRange.from_date;
        inferred.to_date = dateRange.to_date;
    }

    if (!inferred.country) {
        const countryId = inferCountryFromText(conversationText, countriesList);
        if (countryId) {
            inferred.country = countryId;
        }
    } else {
        inferred.country = normalizeCountryId(inferred.country, countriesList) || inferred.country;
    }

    if (!inferred.followers_requirements && /(no|sin|sem)\s+(follower|followers|seguidores|requisito|requirement|minimum|minimo)/.test(normalized)) {
        inferred.followers_requirements = '1';
    }

    const deliverableCounts = getDeliverableMentions(conversationText);
    const deliverableFormats = getDeliverableFormats(conversationText);

    if (hasOpenDeliverablesSignal(conversationText)) {
        inferred.deliverables_mode = 'open';
        inferred.deliverables_counts = {};
        if (deliverableFormats.length) {
            inferred.exchange_options = deliverableFormats;
        }
    } else if (Object.keys(deliverableCounts).length) {
        inferred.deliverables_mode = 'defined';
        inferred.deliverables_counts = {
            ...(inferred.deliverables_counts || {}),
            ...deliverableCounts,
        };
        inferred.exchange_options = Object.keys(deliverableCounts);
    }

    if (!inferred.exchange_options?.length && deliverableFormats.length) {
        inferred.exchange_options = deliverableFormats;
    }

    if (!hasTimeRangeSignal(normalized)) {
        inferred.add_time_range = false;
    }

    if (!inferred.terms_and_conditions && /(create|crear|crea|generate|generar).*(rules|terms|condiciones|conditions|regras|condicoes)/.test(normalized)) {
        inferred.terms_and_conditions = 'Creators must follow the approved brief, publish only original content, respect the agreed campaign dates, disclose the collaboration when required, and avoid any content that could harm the business or FOTH.';
    }

    return inferred;
};

const fieldHasValue = (data = {}, field) => {
    if (field === 'date_range') return Boolean(data.from_date && data.to_date);
    if (field === 'deliverables') return Boolean(data.deliverables_mode && data.exchange_options?.length);
    if (field === 'value' || field === 'quantity' || field === 'sponsor_amount') return Number(data[field]) > 0;
    if (field === 'add_time_range' || field === 'sponsor_this_offer') {
        return data[field] !== undefined && data[field] !== null;
    }
    if (Array.isArray(data[field])) return data[field].length > 0;
    return Boolean(data[field]);
};

const withDeterministicQuestions = (data = {}, conversationText = '', t, compensationMode = 'exchange', countriesList = []) => {
    const inferredData = inferFieldsFromConversation(data, conversationText, countriesList);
    const missingInfo = new Set(
        (Array.isArray(data.missing_info) ? data.missing_info : [])
            .filter(field => !fieldHasValue(inferredData, field))
    );
    const questions = new Set();

    const addQuestion = (field, question) => {
        missingInfo.add(field);
        questions.add(question);
    };

    if (!inferredData.title) {
        addQuestion('title', t('create.ai.questions.title'));
    }

    if (!inferredData.type) {
        addQuestion('type', t('create.ai.questions.type'));
    }

    if (['paid', 'hybrid'].includes(compensationMode)) {
        if (!Number(inferredData.sponsor_amount)) {
            addQuestion('sponsor_amount', t('create.ai.questions.sponsor_amount'));
        }
    } else if (!Number(inferredData.value)) {
        addQuestion('value', t('create.ai.questions.value'));
    }

    if (!Number(inferredData.quantity)) {
        addQuestion('quantity', t('create.ai.questions.quantity'));
    }

    if (!inferredData.country) {
        addQuestion('country', t('create.ai.questions.country'));
    }

    if (!inferredData.description) {
        addQuestion('description', t('create.ai.questions.description'));
    }

    if (!inferredData.from_date || !inferredData.to_date) {
        addQuestion('date_range', t('create.ai.questions.date_range'));
    }

    if (!hasDeliverableSignal(conversationText) || !inferredData.deliverables_mode || !inferredData.exchange_options?.length) {
        addQuestion('deliverables', t('create.ai.questions.deliverables'));
    }

    if (!inferredData.followers_requirements || !hasFollowerSignal(conversationText)) {
        addQuestion('followers_requirements', t('create.ai.questions.followers'));
    }

    if (!inferredData.target_options?.length) {
        addQuestion('target_options', t('create.ai.questions.target'));
    }

    if (!inferredData.terms_and_conditions) {
        addQuestion('terms_and_conditions', t('create.ai.questions.terms'));
    }

    if (inferredData.add_time_range === undefined && hasTimeRangeSignal(conversationText)) {
        addQuestion('add_time_range', t('create.ai.questions.time_range'));
    }

    if (hasSponsorSignal(conversationText) && (!inferredData.sponsor_amount || inferredData.sponsor_amount <= 0)) {
        addQuestion('sponsor_amount', t('create.ai.questions.sponsor_amount'));
    }

    const hasCoreFields = Boolean(
        inferredData.title &&
        inferredData.country &&
        inferredData.from_date &&
        inferredData.to_date &&
        inferredData.quantity &&
        (['paid', 'hybrid'].includes(compensationMode) ? Number(inferredData.sponsor_amount) > 0 : Number(inferredData.value) > 0)
    );

    return {
        ...inferredData,
        missing_info: Array.from(missingInfo),
        questions: Array.from(questions).slice(0, 1),
        confidence: hasCoreFields && questions.size <= 1 ? (data.confidence || 'high') : (questions.size ? 'low' : data.confidence),
    };
};

const getChangedFields = (suggestedData = {}, currentData = {}) => {
    const fields = [
        'title', 'value', 'quantity', 'description', 'type', 'followers_requirements',
        'exchange_options', 'deliverables_mode', 'deliverables_counts', 'target_options',
        'country', 'from_date', 'to_date', 'add_time_range',
        'terms_and_conditions', 'sponsor_this_offer', 'sponsor_amount',
        'compensation_mode', 'cash_currency',
    ];

    const comparable = (field, value) => {
        if (value === undefined || value === null) return value;
        if (field === 'from_date' || field === 'to_date') {
            if (value instanceof Date) return value.toISOString().slice(0, 10);
            return String(value).slice(0, 10);
        }
        if (Array.isArray(value)) return value.map(String).sort();
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
    };

    return fields.reduce((changes, field) => {
        if (suggestedData[field] === undefined) return changes;
        if (comparable(field, suggestedData[field]) !== comparable(field, currentData[field])) {
            changes[field] = suggestedData[field];
        }
        return changes;
    }, {});
};

const AICampaignAssistant = ({
    isOpen,
    onClose,
    onApplyData,
    currentFormData = {},
    mode = 'create',
    compensationMode = 'exchange',
    countries = [],
    openerRef = null,
}) => {
    const { t, i18n } = useTranslation('collab');
    const introMessage = useMemo(() => {
        if (compensationMode === 'paid' || compensationMode === 'hybrid') {
            return t('create.ai.chat_intro_paid', 'Te ayudo a completar este Paid Collab.');
        }
        return t('create.ai.chat_intro_gifting', 'Te ayudo a completar este Gifting Collab.');
    }, [compensationMode, t]);
    const [messages, setMessages] = useState([]);
    const [draftMessage, setDraftMessage] = useState('');
    const [result, setResult] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const recognitionRef = useRef(null);
    const dictationBaseRef = useRef('');
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const dialogRef = useRef(null);
    const abortControllerRef = useRef(null);
    const requestCancelledRef = useRef(false);
    const lastFocusedElementRef = useRef(null);

    const handleClose = useCallback(() => {
        onClose();
        window.setTimeout(() => {
            if (openerRef?.current && typeof openerRef.current.focus === 'function') {
                openerRef.current.focus();
            } else if (lastFocusedElementRef.current && typeof lastFocusedElementRef.current.focus === 'function') {
                try {
                    lastFocusedElementRef.current.focus();
                } catch {
                    // Ignore if element is not focusable
                }
            } else {
                const fallbackBtn = document.getElementById('open-ai-assistant') || document.querySelector('.v2-ai-btn');
                fallbackBtn?.focus();
            }
        }, 50);
    }, [onClose, openerRef]);

    const quickExamples = useMemo(() => [
        {
            id: 'restaurant',
            Icon: Utensils,
            title: t('create.ai.quick_examples.restaurant_title', 'Lanzamiento de restaurante'),
            detail: t('create.ai.quick_examples.restaurant_detail', 'Food creators · 5 plazas · desde $80'),
            prompt: t('create.ai.examples.restaurant'),
        },
        {
            id: 'event',
            Icon: CalendarDays,
            title: t('create.ai.quick_examples.event_title', 'Evento / rooftop'),
            detail: t('create.ai.quick_examples.event_detail', 'Lifestyle · 8 plazas · horario nocturno'),
            prompt: t('create.ai.examples.event'),
        },
        {
            id: 'pr',
            Icon: Package,
            title: t('create.ai.quick_examples.pr_title', 'Envío de PR kits'),
            detail: t('create.ai.quick_examples.pr_detail', 'Beauty creators · 12 kits · 1 Reel'),
            prompt: t('create.ai.examples.pr'),
        },
    ], [t]);

    const resultQuestions = getFollowUpItems(result || {});
    const hasMissingInfo = Boolean(resultQuestions.length);
    const previewChecks = [
        result?.title,
        result?.country,
        result?.from_date && result?.to_date,
        result?.value || result?.sponsor_amount,
        result?.quantity,
        result?.exchange_options?.length,
        result?.target_options?.length,
    ];
    const completedPreviewFields = previewChecks.filter(Boolean).length;

    const cancelGeneration = useCallback(() => {
        if (!isGenerating) return;
        requestCancelledRef.current = true;
        abortControllerRef.current?.abort();
    }, [isGenerating]);

    useEffect(() => {
        if (!isOpen) return;

        lastFocusedElementRef.current = document.activeElement;
        setMessages([{ role: 'assistant', content: introMessage }]);
        setDraftMessage('');
        setResult(null);
    }, [introMessage, isOpen]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const handleEscape = (event) => {
            if (event.key !== 'Escape') return;
            if (isGenerating) {
                cancelGeneration();
            } else {
                handleClose();
            }
        };
        document.addEventListener('keydown', handleEscape);
        window.setTimeout(() => dialogRef.current?.querySelector('textarea')?.focus(), 80);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [cancelGeneration, handleClose, isOpen, isGenerating]);

    useEffect(() => () => abortControllerRef.current?.abort(), []);

    useEffect(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        textarea.style.height = 'auto';
        textarea.style.height = `${Math.min(textarea.scrollHeight, 140)}px`;
    }, [draftMessage]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [messages, isGenerating]);

    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) return undefined;

        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = true;
        recognitionRef.current.interimResults = true;
        recognitionRef.current.lang = i18n.language?.startsWith('pt') ? 'pt-BR' : i18n.language?.startsWith('en') ? 'en-US' : 'es-ES';

        recognitionRef.current.onresult = (event) => {
            let transcript = '';
            for (let i = 0; i < event.results.length; i += 1) {
                transcript += event.results[i][0].transcript;
            }
            setDraftMessage(dictationBaseRef.current ? `${dictationBaseRef.current} ${transcript}` : transcript);
        };

        recognitionRef.current.onerror = () => {
            setIsListening(false);
            setMessages(prev => [...prev, { role: 'assistant', content: t('create.ai.errors.mic'), isError: true }]);
        };

        recognitionRef.current.onend = () => setIsListening(false);

        return () => recognitionRef.current?.stop();
    }, [i18n.language, t]);

    const toggleListening = () => {
        if (isListening) {
            recognitionRef.current?.stop();
            setIsListening(false);
            return;
        }

        if (!recognitionRef.current) {
            setMessages(prev => [...prev, { role: 'assistant', content: t('create.ai.errors.unsupported_voice'), isError: true }]);
            return;
        }

        dictationBaseRef.current = draftMessage;
        recognitionRef.current.start();
        setIsListening(true);
    };

    const buildPrompt = (conversationText, accumulatedData = {}) => {
        const baseData = {
            ...currentFormData,
            ...(accumulatedData || {}),
        };
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0];
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowStr = tomorrow.toISOString().split('T')[0];
        const maxDate = new Date(tomorrow);
        maxDate.setDate(maxDate.getDate() + 30);
        const maxDateStr = maxDate.toISOString().split('T')[0];

        const existingData = compactPromptData({
            title: baseData.title || '',
            value: ['paid', 'hybrid'].includes(compensationMode) ? undefined : baseData.value || '',
            in_kind_value: ['paid', 'hybrid'].includes(compensationMode) ? baseData.value || '' : undefined,
            quantity: baseData.quantity || '',
            description: stripHtml(baseData.description || ''),
            type: baseData.type || '',
            country: baseData.country || '',
            followers_requirements: baseData.followers_requirements || '',
            exchange_options: baseData.exchange_options || [],
            deliverables_mode: baseData.deliverables_mode || 'defined',
            deliverables_counts: baseData.deliverables_counts || {},
            target_options: baseData.target_options || [],
            terms_and_conditions: stripHtml(baseData.terms_and_conditions || ''),
            add_time_range: Boolean(baseData.add_time_range),
            sponsor_this_offer: Boolean(baseData.sponsor_this_offer),
            sponsor_amount: baseData.sponsor_amount || '',
            compensation_mode: baseData.compensation_mode || compensationMode,
            cash_currency: baseData.cash_currency || 'USD',
            from_date: baseData.from_date || '',
            to_date: baseData.to_date || '',
            missing_info: baseData.missing_info || [],
        });

        return `
You are FOTH AI, a senior marketing operations assistant for fast influencer collab creation.
Return strict JSON only. Infer safe defaults for common operational fields, but do not invent business-critical details.
Language for generated copy: ${i18n.language || 'es'}.
Form mode: ${mode}. Compensation mode: ${compensationMode}. If mode is edit, preserve existing values unless the user asks to change them.
For paid/hybrid collaborations sponsor_amount is the cash payment to ONE creator; quantity is the number of positions. Never copy that payment or the total budget into value or in_kind_value.
in_kind_value is optional: populate it only when the business explicitly specifies the monetary value of an additional product, meal, or other non-cash benefit. Do not estimate a product price, duplicate the cash payment, or ask for this optional field. Use 0 only if the business explicitly removes the benefit. For exchange collaborations value means the non-cash exchange value.

Valid IDs:
type: "1" Restaurants & Nightlife, "2" Retail, "3" Experiences, "4" Events, "5" Clubs, "6" Hotels, "7" Fitness, "8" Wellness & Beauty, "9" Travel, "10" PR Kits, "11" Other.
followers_requirements: "1" No requirements, "2" Friends of the house, "3" 3000 - 15000, "4" +15000.
exchange_options: "1" Post, "2" Story, "3" Reel.
target_options: "1" Lifestyle, "2" Fashion, "3" Beauty, "4" Food, "5" Wellness, "6" Entertainment, "7" Travel, "8" Sports, "9" Tech.

Date Constraints (Strict):
Today's reference date is ${todayStr}.
Any campaign start date (from_date) must be in the future (on or after tomorrow: ${tomorrowStr}).
The campaign duration (from_date to to_date) CANNOT exceed 30 days. Maximum allowed to_date if starting tomorrow is ${maxDateStr}.
If the user provides vague or relative dates (e.g. "este fin de semana", "este mes", "la próxima semana") or dates without year ("del 15 al 25 de octubre"), infer practical future dates in YYYY-MM-DD format within the 30-day limit using the current year (${today.getFullYear()}) or next year if the month has already passed.

Participating Countries & Numeric IDs:
- Panamá: "11" (code: PA, aliases: panama, panama city, ciudad de panama, pty)
- Colombia: "12" (code: CO, aliases: colombia, bogota, medellin, cali, barranquilla, cartagena)
- México: "14" (code: MX, aliases: mexico, cdmx, ciudad de mexico, guadalajara, monterrey, cancun, puebla)
- Costa Rica: "15" (code: CR, aliases: costa rica, san jose, heredia, alajuela)
- El Salvador: "16" (code: SV, aliases: el salvador, san salvador, santa ana)
- Perú: "17" (code: PE, aliases: peru, lima, cusco, arequipa, trujillo)
- Brasil: "18" (code: BR, aliases: brasil, brazil, sao paulo, rio de janeiro, rio, brasilia)
- Argentina: "19" (code: AR, aliases: argentina, buenos aires, cordoba, rosario, mendoza)
IMPORTANT: For the "country" field, ALWAYS return the numeric ID string (e.g. "11", "12", "14", "17", etc.).
Recognize country names and cities even if written without accents or in lowercase (e.g. "Panama", "Mexico", "Peru", "Bogota", "CDMX").
Chile (CL) and the United States (US) are NOT participating countries.

Choose deliverables_mode "open" when the user wants creators to decide formats, otherwise "defined".
Keep description and terms_and_conditions plain text, concise, polished, and ready to publish.
If dates are mentioned or implied, populate from_date and to_date as YYYY-MM-DD.
This is a chat experience. First understand the user's short description, then detect what is still missing to complete the collab form.
Reservations and bookings are permanently disabled for collabs. Never include a reservation field, infer a reservation setting, or ask any question about reservations or bookings.
Do not invent business-critical details. Ask short, direct follow-up questions for missing required information:
- cash payment per creator for paid/hybrid, or exchange value for exchange collaborations
- number of creators
- campaign start and end dates
- country
- deliverables: open to creators or defined by the business; if defined, ask how many posts, reels, or stories
- target audience
- collaboration type/category
- follower requirement
- whether a time range should be shown
- terms and conditions
- sponsorship amount when sponsorship is requested
${compensationMode === 'paid' ? '- payment per creator and currency for this paid collaboration' : ''}
Return exactly one follow-up question at a time, choosing the single most important missing detail.
If the information is already provided in the conversation or in existing form data, DO NOT ask for it again.
Be precise about deliverables. If the user says creators can publish as many as they want, use deliverables_mode "open". If the user gives exact quantities like "1 Reel and 2 Stories", use deliverables_mode "defined" and fill deliverables_counts with the right IDs.
Do not ask about featured image or additional images because the form handles image upload separately.
Set confidence to "high" only when the form can be filled without follow-up questions. Use "medium" when defaults were inferred. Use "low" when important details are missing.
If important details are missing, still return any fields you can infer, but put the missing items in missing_info and natural follow-up questions in questions.

Existing form data:
${JSON.stringify(existingData, null, 2)}

Conversation so far:
${conversationText}

JSON schema:
{
  "title": "string",
  "value": number,
  "in_kind_value": number | null,
  "quantity": number,
  "description": "string",
  "type": "string",
  "followers_requirements": "string",
  "exchange_options": ["string"],
  "deliverables_mode": "open" | "defined",
  "deliverables_counts": {"1": number, "2": number, "3": number},
  "target_options": ["string"],
  "country": "string",
  "from_date": "YYYY-MM-DD",
  "to_date": "YYYY-MM-DD",
  "add_time_range": boolean,
  "terms_and_conditions": "string",
  "sponsor_this_offer": boolean,
  "sponsor_amount": number,
  "compensation_mode": "exchange" | "paid" | "hybrid",
  "cash_currency": "USD",
  "missing_info": ["field_name"],
  "questions": ["short question for the user"],
  "confidence": "high" | "medium" | "low",
  "response_message": "string"
}`;
    };

    const formatConversationForPrompt = (nextMessages) => nextMessages.slice(-6)
        .map((message) => `${message.role === 'assistant' ? 'FOTH AI' : 'Business user'}: ${message.content}`)
        .join('\n\n');

    const buildAssistantMessage = (data) => {
        const questions = getFollowUpItems(data);
        const safeResponseMessage = hasBookingLanguage(data.response_message)
            ? ''
            : data.response_message;
        const message = questions.length
            ? t('create.ai.chat_followup')
            : safeResponseMessage || t('create.ai.chat_ready');

        if (!questions.length) return message;

        return `${message}\n\n${questions.map((question) => `• ${question}`).join('\n')}`;
    };

    const handleSend = async (messageOverride = null) => {
        const message = (typeof messageOverride === 'string' && messageOverride.trim())
            ? messageOverride
            : draftMessage;
        const trimmedMessage = String(message || '').trim();
        if (!trimmedMessage) return;

        setIsGenerating(true);
        if (isListening) recognitionRef.current?.stop();

        const controller = new AbortController();
        abortControllerRef.current = controller;
        requestCancelledRef.current = false;

        const nextMessages = [...messages, { role: 'user', content: trimmedMessage }];
        setMessages(nextMessages);
        setDraftMessage('');

        try {
            const response = await api.post('/collabs/ai-campaign-assistant/', {
                prompt: buildPrompt(formatConversationForPrompt(nextMessages), result),
            }, {
                signal: controller.signal,
                // Gemini can take longer than the global API timeout while it
                // prepares the structured campaign response.
                timeout: 90000,
            });
            const parsed = response.data;
            const merged = { ...(result || {}) };
            if (parsed && typeof parsed === 'object') {
                Object.entries(parsed).forEach(([k, v]) => {
                    if (v !== undefined && v !== null && v !== '') {
                        if (Array.isArray(v) && v.length === 0 && Array.isArray(merged[k]) && merged[k].length > 0) {
                            return;
                        }
                        merged[k] = v;
                    }
                });
            }
            const normalized = withDeterministicQuestions(
                normalizeGeneratedData(merged, compensationMode, countries),
                formatConversationForPrompt(nextMessages),
                t,
                compensationMode,
                countries
            );
            setResult(normalized);
            setMessages(prev => [...prev, { role: 'assistant', content: buildAssistantMessage(normalized) }]);
        } catch (err) {
            const wasCancelled = requestCancelledRef.current
                || err?.code === 'ERR_CANCELED'
                || err?.name === 'CanceledError';
            if (wasCancelled) {
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: t('create.ai.cancelled', 'Generación cancelada.'),
                    isError: true,
                }]);
                return;
            }
            const errorCode = err.response?.data?.code;
            const baseErrorMessage = errorCode === 'gemini_not_configured'
                ? t('create.ai.errors.missing_key')
                : err.code === 'ECONNABORTED'
                    ? t('create.ai.errors.timeout', 'La respuesta está tardando demasiado. Intenta de nuevo.')
                : t('create.ai.errors.generate');
            const serverDetail = err.response?.data?.detail;
            const errorMessage = serverDetail && errorCode !== 'gemini_not_configured'
                ? `${baseErrorMessage}\n${serverDetail}`
                : baseErrorMessage;
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: errorMessage,
                isError: true,
                retryText: trimmedMessage,
            }]);
        } finally {
            if (abortControllerRef.current === controller) abortControllerRef.current = null;
            setIsGenerating(false);
        }
    };

    const handleExampleClick = (example) => {
        // Quick examples are intentionally one-click: the AI can ask for the
        // one missing detail after it has already prepared the first draft.
        handleSend(example);
    };

    const handleInputKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSend();
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (isGenerating) {
            cancelGeneration();
            return;
        }
        handleSend();
    };

    const handleBackdropMouseDown = (event) => {
        if (event.target === event.currentTarget && !isGenerating) handleClose();
    };

    const handleDialogKeyDown = (event) => {
        if (event.key !== 'Tab') return;
        const focusable = dialogRef.current?.querySelectorAll(
            'button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), a[href]'
        );
        if (!focusable?.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };

    const applyFields = () => {
        const changes = getChangedFields(result, currentFormData);
        onApplyData(changes);
        handleClose();
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="ai-modal-overlay" onMouseDown={handleBackdropMouseDown}>
                <motion.div
                    initial={{ opacity: 0, y: 28, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 28, scale: 0.98 }}
                    transition={{ duration: 0.28, ease: [0.23, 1, 0.32, 1] }}
                    className="ai-chat-container"
                    ref={dialogRef}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="foth-ai-title"
                    onKeyDown={handleDialogKeyDown}
                >
                    <div className="ai-header">
                        <div>
                            <div className="ai-title" id="foth-ai-title"><Sparkles size={18} /> {t('create.ai.title')}</div>
                            <p>{t('create.ai.subtitle')}</p>
                        </div>
                        <button
                            type="button"
                            onClick={isGenerating ? cancelGeneration : handleClose}
                            className="close-btn"
                            aria-label={isGenerating ? t('create.ai.cancel', 'Cancelar generación') : t('create.ai.close')}
                        >
                            <X size={18} />
                        </button>
                    </div>

                    <div className={`ai-chat-body ${result ? 'has-preview' : ''}`}>
                        <div className="ai-conversation-column">
                            {!result && (
                                <section className="ai-quick-start" aria-labelledby="ai-quick-start-title">
                                    <div className="ai-quick-start-heading">
                                        <div>
                                            <span className="ai-section-kicker"><Sparkles size={13} /> {t('create.ai.quick_start_kicker', 'Empieza en 1 clic')}</span>
                                            <h3 id="ai-quick-start-title">{t('create.ai.quick_start_title', '¿Qué vas a activar en Panamá?')}</h3>
                                            <p>{t('create.ai.quick_start_subtitle', 'Elige una base y FOTH AI te preguntará solo lo que falte.')}</p>
                                        </div>
                                        <span className="ai-country-pill"><MapPin size={13} /> Panamá</span>
                                    </div>
                                    <div className="ai-quick-example-grid">
                                        {quickExamples.map(({ id, Icon, title, detail, prompt }) => (
                                            <button
                                                type="button"
                                                className="ai-quick-example"
                                                key={id}
                                                onClick={() => handleExampleClick(prompt)}
                                                disabled={isGenerating}
                                            >
                                                <span className="ai-quick-icon"><Icon size={17} /></span>
                                                <span className="ai-quick-copy"><strong>{title}</strong><small>{detail}</small></span>
                                                <ArrowRight className="ai-quick-arrow" size={16} />
                                            </button>
                                        ))}
                                    </div>
                                    <div className="ai-quick-footnote"><Users size={14} /> {t('create.ai.quick_start_empty', 'También puedes escribir tu idea completa abajo.')}</div>
                                </section>
                            )}

                            <div className="ai-message-list" aria-live="polite">
                            {messages.map((message, index) => (
                                <motion.div
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`${message.role}-${index}`}
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className={`ai-message ${message.role === 'user' ? 'user' : 'assistant'} ${message.isError ? 'error' : ''}`}
                                >
                                    <div className="ai-message-label">
                                        {message.role === 'user' ? t('create.ai.chat_you') : 'FOTH AI'}
                                    </div>
                                    <div className="ai-message-bubble">{message.content}</div>
                                    {message.retryText && (
                                        <button type="button" className="ai-retry-btn" onClick={() => handleSend(message.retryText)}>
                                            {t('create.ai.retry', 'Try again')}
                                        </button>
                                    )}
                                </motion.div>
                            ))}
                            {isGenerating && (
                                <div className="ai-message assistant">
                                    <div className="ai-message-label">FOTH AI</div>
                                    <div className="ai-message-bubble loading">
                                        <Loader2 className="spin" size={16} />
                                        {t('create.ai.generating')}
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                            </div>
                        </div>

                        <AnimatePresence>
                            {result && (
                                <motion.aside
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 12 }}
                                    className="ai-data-preview"
                                >
                                    <div className="preview-topline">
                                        <div>
                                            <span className="preview-kicker"><Sparkles size={13} /> {t('create.ai.preview_kicker', 'Borrador inteligente')}</span>
                                            <h3>{t('create.ai.preview_title')}</h3>
                                        </div>
                                        <span className={`preview-status ${hasMissingInfo ? 'needs-attention' : 'ready'}`}>
                                            {hasMissingInfo ? <AlertCircle size={13} /> : <CheckCircle2 size={13} />}
                                            {hasMissingInfo ? t('create.ai.needs_status', 'Falta 1 dato') : t('create.ai.ready_status', 'Listo para revisar')}
                                        </span>
                                    </div>
                                    <div className="preview-progress" aria-label={`${completedPreviewFields} de ${previewChecks.length} campos clave listos`}>
                                        <div className="preview-progress-copy"><span>{t('create.ai.fields_ready', 'Campos clave')}</span><strong>{completedPreviewFields}/{previewChecks.length}</strong></div>
                                        <div className="preview-progress-track"><span style={{ width: `${(completedPreviewFields / previewChecks.length) * 100}%` }} /></div>
                                    </div>
                                    {hasMissingInfo && (
                                        <div className="ai-missing-info">
                                            <div className="ai-missing-title">
                                                <AlertCircle size={15} />
                                                {t('create.ai.missing_title')}
                                            </div>
                                            <ul>
                                                {resultQuestions.map((question) => (
                                                    <li key={question}>{question}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    <div className="preview-row"><strong>{t('create.form.title_label')}</strong><span>{result.title || '-'}</span></div>
                                    <div className="preview-row"><strong>{t('create.form.country_label', 'País')}</strong><span>{getCountryDisplayName(result.country, countries) || '-'}</span></div>
                                    <div className="preview-row"><strong>{t('create.form.dates_label', 'Fechas')}</strong><span>{result.from_date && result.to_date ? `${result.from_date} — ${result.to_date}` : '-'}</span></div>
                                    <div className="preview-row"><strong>{t('create.form.budget_label')}</strong><span>{['paid', 'hybrid'].includes(result.compensation_mode) ? `${result.cash_currency || 'USD'} ${result.sponsor_amount || 0}` : `$${result.value || 0}`}</span></div>
                                    <div className="preview-row"><strong>{t('create.form.creators_label')}</strong><span>{result.quantity || '-'}</span></div>
                                    <div className="preview-row"><strong>{t('create.form.exchange_label')}</strong><span>{result.exchange_options?.length || 0}</span></div>
                                    <div className="preview-row"><strong>{t('create.form.target_label')}</strong><span>{result.target_options?.length || 0}</span></div>
                                    {hasMissingInfo && (
                                        <p className="ai-response strong">{t('create.ai.answer_help')}</p>
                                    )}
                                    <button type="button" className="apply-data-btn" onClick={applyFields}>
                                        <Check size={16} /> {hasMissingInfo ? t('create.ai.apply_draft') : t('create.ai.apply')}
                                    </button>
                                </motion.aside>
                            )}
                        </AnimatePresence>
                    </div>

                    <form className="ai-input-area" onSubmit={handleSubmit}>
                        <button
                            type="button"
                            className={`mic-btn ${isListening ? 'listening' : ''}`}
                            onClick={toggleListening}
                            title={t('create.ai.dictate')}
                            disabled={isGenerating}
                        >
                            {isListening ? <Mic size={18} /> : <MicOff size={18} />}
                        </button>
                        <div className="ai-textarea-wrap">
                            <textarea
                                ref={textareaRef}
                                value={draftMessage}
                                onChange={(event) => setDraftMessage(event.target.value)}
                                onKeyDown={handleInputKeyDown}
                                placeholder={hasMissingInfo ? t('create.ai.answer_placeholder') : t('create.ai.chat_placeholder')}
                                rows={1}
                                maxLength={4000}
                                aria-label={t('create.ai.chat_input_label')}
                            />
                            <span className="ai-input-meta">{hasMissingInfo ? t('create.ai.answer_prompt') : t('create.ai.input_meta', 'Enter para enviar · Shift + Enter para otra línea')}</span>
                        </div>
                        <button
                            type="submit"
                            className={`send-btn ${isGenerating ? 'cancel' : ''}`}
                            disabled={!isGenerating && !draftMessage.trim()}
                        >
                            {isGenerating ? <X size={17} /> : <Send size={17} />}
                            <span>{isGenerating ? t('create.ai.cancel', 'Cancelar') : hasMissingInfo ? t('create.ai.update') : t('create.ai.send')}</span>
                        </button>
                    </form>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default AICampaignAssistant;
